import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import Card from '../ui/Card';

const COLORS = ['#1B2A4A', '#2D5F8A', '#4ECDC4', '#27AE60', '#F39C12', '#E74C3C', '#9B59B6', '#3498DB'];

interface ChartProps {
  data: { name: string; value: number }[];
  title: string;
}

export const DepartmentPieChart: React.FC<ChartProps> = ({ data, title }) => (
  <Card header={<span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>{title}</span>}>
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={3}
          dataKey="value"
          nameKey="name"
        >
          {data.map((_, index) => (
            <Cell key={index} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: '1px solid var(--color-border-light)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 'var(--font-size-sm)',
          }}
        />
        <Legend
          formatter={(value) => <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  </Card>
);

export const ObligationBarChart: React.FC<ChartProps> = ({ data, title }) => (
  <Card header={<span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>{title}</span>}>
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
        <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
        <YAxis tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: '1px solid var(--color-border-light)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 'var(--font-size-sm)',
          }}
        />
        <Bar dataKey="value" fill="var(--color-secondary)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </Card>
);

interface LineChartProps {
  data: { date: string; score: number }[];
  title: string;
}

export const AuditScoreChart: React.FC<LineChartProps> = ({ data, title }) => (
  <Card header={<span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>{title}</span>}>
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
        <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: '1px solid var(--color-border-light)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 'var(--font-size-sm)',
          }}
        />
        <Line
          type="monotone"
          dataKey="score"
          stroke="var(--color-accent)"
          strokeWidth={2.5}
          dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </Card>
);

interface CircularProgressProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  label?: string;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 120,
  strokeWidth = 10,
  color = 'var(--color-accent)',
  label,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-border-light)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            transform: 'rotate(90deg)',
            transformOrigin: 'center',
            fontSize: size * 0.22,
            fontWeight: 700,
            fill: 'var(--color-text)',
            fontFamily: 'var(--font-family)',
          }}
        >
          {value}%
        </text>
      </svg>
      {label && (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 500, textAlign: 'center' }}>
          {label}
        </span>
      )}
    </div>
  );
};
